import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { removeBase } from '~/shared/helpers/string-helpers';
import { useAppSelector } from '~/shared/store/hooks';
import { getAppLanguage } from '~/shared/store/slices/app/selectors';

import 'jodit/es2021/jodit.min.css';
import './html-area-field.scss';

const JoditEditor = dynamic(() => import('jodit-react'), { ssr: false });

const getJoditLocale = (lng?: string | null) => {
  const locale = String(lng ?? 'ua').toLowerCase();

  // Jodit's built-in Ukrainian locale code is `ua`, not `uk`.
  if (locale === 'ua' || locale === 'uk' || locale.startsWith('uk-')) return 'ua';
  if (locale === 'ru' || locale.startsWith('ru-')) return 'ru';

  return 'en';
};

type HTMLAreaFieldPropsType = {
  value: string;
  config: Record<string, unknown>;
  name: string;
  setValue: (fieldName: string, value: string, options: Record<string, unknown>) => void;
  onChange: (value: string) => void;
};

const HTMLAreaField = ({ value, name, setValue, onChange, config }: HTMLAreaFieldPropsType) => {
  const lng = useAppSelector(getAppLanguage);
  const locale = getJoditLocale(lng);
  const editorRef = useRef<any>(null);
  const configRef = useRef(config);
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(value ?? '');
  const fieldNameRef = useRef(name);
  const lastEmittedValueRef = useRef<string | null>(null);

  configRef.current = config;
  onChangeRef.current = onChange;

  if (fieldNameRef.current !== name) {
    fieldNameRef.current = name;
    initialValueRef.current = value ?? '';
    lastEmittedValueRef.current = null;
  }

  // Do not depend on the `config` object identity here. `getContentFieldConfig`
  // returns a new object when RHF rerenders, and rebuilding Jodit config on each
  // keystroke makes the editor lose its selection/focus.
  const editorConfig = useMemo(
    () => ({
      ...configRef.current,
      height: configRef.current?.height ?? 600,
      language: locale,
    }),
    [locale],
  );

  const emitChange = useCallback((content: string) => {
    const normalized = removeBase(content);
    if (lastEmittedValueRef.current === normalized) return;
    // Record BEFORE notifying RHF: its synchronous echo must not rewrite Jodit.
    lastEmittedValueRef.current = normalized;
    onChangeRef.current(normalized);
  }, []);

  // Jodit is intentionally uncontrolled while the user types. RHF still gets
  // every change through emitChange, but the changed RHF value is not pumped
  // back into Jodit's `value` prop. External form resets / async replacements
  // are applied only while the editor itself is not focused.
  useEffect(() => {
    const editor = editorRef.current;
    const nextValue = value ?? '';

    if (!editor || typeof document === 'undefined') return;
    // RHF echoes each keystroke, sometimes before activeElement settles.
    // Assigning editor.value here destroys the caret/selection.
    if (nextValue === lastEmittedValueRef.current) return;

    const editorElement = editor?.editor as HTMLElement | undefined;
    const activeElement = editorElement?.ownerDocument?.activeElement ?? document.activeElement;
    const isFocused = Boolean(editor.isFocused || editorElement?.contains(activeElement));

    if (!isFocused && editor.value !== nextValue) {
      editor.value = nextValue;
      initialValueRef.current = nextValue;
      lastEmittedValueRef.current = null;
    }
  }, [value]);

  return (
    <div className={`html-area-field html-area-field--${locale}`}>
      <JoditEditor
        ref={editorRef}
        key={`jodit-${name}-${locale}`}
        value={initialValueRef.current}
        config={editorConfig as any}
        onChange={emitChange}
        onBlur={emitChange}
      />
    </div>
  );
};

export default HTMLAreaField;

